import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { HandHeart, FloppyDisk } from "@phosphor-icons/react";
import { todayLocalDateString } from "@/lib/localDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog for logging a friendly / exhibition match.
 * Opens from PlayerProfile or a global "Log Friendly" button.
 * Default player1_id = current profile (if passed); player2 is chosen from the list.
 */
export default function LogFriendlyDialog({
  open, onOpenChange, defaultPlayer1Id = null, onCreated,
}) {
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState(initialForm(defaultPlayer1Id));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialForm(defaultPlayer1Id));
    axios.get(`${API}/players?include_external=false`)
      .then(r => setPlayers(r.data || []))
      .catch(() => {});
  }, [open, defaultPlayer1Id]);

  const submit = async () => {
    if (!form.player1_id || !form.player2_id) {
      toast.error("Select both players"); return;
    }
    if (form.player1_id === form.player2_id) {
      toast.error("Player 1 and Player 2 must be different"); return;
    }
    const p1s = Number(form.player1_score), p2s = Number(form.player2_score);
    if (Number.isNaN(p1s) || Number.isNaN(p2s) || p1s < 0 || p2s < 0) {
      toast.error("Scores must be 0 or greater"); return;
    }
    if (p1s === p2s) {
      toast.error("Friendly cannot end in a draw"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/friendly-matches`, {
        player1_id: form.player1_id,
        player2_id: form.player2_id,
        player1_score: p1s,
        player2_score: p2s,
        date_played: form.date_played,
        venue: form.venue || null,
        player1_highest_break: Number(form.player1_highest_break) || 0,
        player2_highest_break: Number(form.player2_highest_break) || 0,
        notes: form.notes || null,
      }, { withCredentials: true });
      toast.success("Friendly match recorded");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record friendly");
    } finally { setSubmitting(false); }
  };

  const otherPlayers = (excludeId) => players.filter(p => p.id !== excludeId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="log-friendly-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandHeart className="w-5 h-5 text-primary" weight="fill" />
            Log Friendly Match
          </DialogTitle>
          <DialogDescription>
            Exhibition / friendly games are recorded on player profiles + head-to-head stats only.
            They do <strong>not</strong> affect ranking points or ELO.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Player 1 <span className="text-destructive">*</span></Label>
              <Select value={form.player1_id} onValueChange={v => setForm({ ...form, player1_id: v })}>
                <SelectTrigger data-testid="friendly-p1-select"><SelectValue placeholder="Select player" /></SelectTrigger>
                <SelectContent>
                  {otherPlayers(form.player2_id).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Player 2 <span className="text-destructive">*</span></Label>
              <Select value={form.player2_id} onValueChange={v => setForm({ ...form, player2_id: v })}>
                <SelectTrigger data-testid="friendly-p2-select"><SelectValue placeholder="Select opponent" /></SelectTrigger>
                <SelectContent>
                  {otherPlayers(form.player1_id).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">P1 Frames <span className="text-destructive">*</span></Label>
              <Input
                type="number" min="0" value={form.player1_score}
                onChange={e => setForm({ ...form, player1_score: e.target.value })}
                data-testid="friendly-p1-score" />
            </div>
            <div>
              <Label className="text-xs">P2 Frames <span className="text-destructive">*</span></Label>
              <Input
                type="number" min="0" value={form.player2_score}
                onChange={e => setForm({ ...form, player2_score: e.target.value })}
                data-testid="friendly-p2-score" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">P1 Highest Break</Label>
              <Input
                type="number" min="0" value={form.player1_highest_break}
                onChange={e => setForm({ ...form, player1_highest_break: e.target.value })}
                data-testid="friendly-p1-hb" />
            </div>
            <div>
              <Label className="text-xs">P2 Highest Break</Label>
              <Input
                type="number" min="0" value={form.player2_highest_break}
                onChange={e => setForm({ ...form, player2_highest_break: e.target.value })}
                data-testid="friendly-p2-hb" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date Played</Label>
              <Input
                type="date" value={form.date_played}
                onChange={e => setForm({ ...form, date_played: e.target.value })}
                data-testid="friendly-date" />
            </div>
            <div>
              <Label className="text-xs">Venue (optional)</Label>
              <Input
                placeholder="e.g. DSM Snooker Hall"
                value={form.venue}
                onChange={e => setForm({ ...form, venue: e.target.value })}
                data-testid="friendly-venue" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={2} placeholder="Anything to remember about this match…"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              data-testid="friendly-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="friendly-submit-btn">
            <FloppyDisk className="w-4 h-4 mr-1" />
            {submitting ? "Saving…" : "Record Match"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initialForm(defaultP1) {
  return {
    player1_id: defaultP1 || "",
    player2_id: "",
    player1_score: "",
    player2_score: "",
    player1_highest_break: "",
    player2_highest_break: "",
    date_played: todayLocalDateString(),
    venue: "",
    notes: "",
  };
}
